<template>
  <div v-if="isLoading" class="bg-gray-100 p-2 rounded-md text-center my-2"> {{ loadingText || 'Please wait' }}</div>
  <div class="flex flex-col gap-5 p-3" :class="recentLocations.length === 0 ? 'pb-10' : ''">
    <div class="flex flex-col gap-1">
      <h2 class="text-gray-900 font-medium text-lg">Select Delivery Location</h2>
    </div>

    <V2LocationFinderComponent @success="navigate" @error="handleError" />
    <V2GeolocationComponent @success="navigate" @error="handleError" />

    <!-- Recent Locations -->
    <div v-if="recentLocations.length > 0" class="flex flex-col gap-2 my-2 w-full pr-2">
      <UDivider label="OR" />
      <p class="text-[20px] md:font-medium font-bold text-left">Recent Locations</p>
      <div class="rounded-md cursor-pointer flex justify-between items-center hover:bg-gray-50 transition"
        v-for="location in recentLocations.slice(0, 3)" :key="location.id" @click="setRecentLocation(location)">
        <div class="flex items-start gap-2 w-[80%]">
          <UIcon name="i-heroicons-clock" class="h-5 w-5 md:h-6 md:w-6 text-primary flex shrink-0" />
          <p class="text-gray-700 text-left text-sm">
            {{ location.formatted_address }}
          </p>
        </div>
        <UIcon name="i-heroicons-arrow-up-right" class="h-5 w-5 md:h-6 md:w-6 text-primary" />
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, onMounted } from "vue";
import { GoogleAnalytics } from "~/google_analytics/events";
import { EventParameter } from "~/google_analytics/events.enum";
import { getRecentAddresses } from "~/utility/location/recent.location.storage";
const emit = defineEmits(["success", "close"]);

const isLoading = ref(false);

const geolocation = useGeoLocation();

const gps = computed(() => geolocation.selectedAddress?.gps);
const hasLocation = computed(() => !!(gps.value?.latitude && gps.value?.longitude));
const latitude = computed(() => gps.latitude || 0);
const longitude = computed(() => gps.longitude || 0);

const loadingText = ref("");
const recentLocations = ref([]);

const addressText = computed(() => geolocation.formatSelectedAddress);

const navigate = async () => {
  isLoading.value = true;
  setTimeout(() => {
    isLoading.value = false;
    emit("success");
  }, 1000);
};

const handleError = (message) => {
  loadingText.value = message;
  isLoading.value = true;
};

onMounted(() => {
  recentLocations.value = getRecentAddresses();
  GoogleAnalytics.popUpView({
    [EventParameter.POP_UP_NAME]: 'location_picker',
    [EventParameter.POP_UP_TITLE]: 'select_delivery_location'
  })
});

const setRecentLocation = async (loc) => {

  const isSame =
    geolocation.selectedAddress?.gps?.latitude === loc?.gps?.latitude &&
    geolocation.selectedAddress?.gps?.longitude === loc?.gps?.longitude;

  if (isSame) {
    loadingText.value = "This location is already selected.";
    isLoading.value = true;
    return;
  }

  GoogleAnalytics.locationEvent({
    [EventParameter.ACTION]: 'location_selected_from_recent',
    [EventParameter.LATITUDE]: latitude.value,
    [EventParameter.LONGITUDE]: longitude.value,
    [EventParameter.ADDRESS]: addressText.value || 'none',
    [EventParameter.REASON]: 'unknown',
    [EventParameter.LOCATION_AVAILABLE]: hasLocation.value ? "yes" : "no",
    [EventParameter.PROMPT_TYPE]: "unknown",
    [EventParameter.NEEDS_LOCATION_FETCH]: hasLocation.value ? "no" : "yes",
  });

  await geolocation.setSelectedAddress(loc);

  isLoading.value = false;
  loadingText.value = "";

  navigate();
};

</script>
